import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  ExpandMore,
  ChevronRight,
  FolderRounded,
  HomeRounded,
  Search,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { fontSize } from "../../component/theme/fontSize";
import { ThemeColor } from "../../component/theme/color";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import {
  GetAllFoldersService,
  UpdateFileService,
  UpdateFolderService,
} from "../../Redux/service/FileManagerService";
import CustomButton from "../../component/button/button";
import {
  buildFolderOptions,
  buildVisibleRows,
  findSearchRoots,
} from "./moveToTree";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: 560, md: 680 },
  maxWidth: "100%",
  height: { xs: "88vh", sm: "80vh" },
  maxHeight: "88vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 24px 60px rgba(16, 24, 40, 0.22)",
  p: { xs: 2.5, sm: 3 },
  outline: "none",
};

// Modal to move a file/folder anywhere: pick "My Folder (Root)" or any folder.
// Folders with children can be collapsed/expanded, and the search box filters
// the whole tree by name.
// item: { id, name, type: "file" | "folder" }
// onMoved: refresh callback after a successful move
export const MoveToModal = ({ open, handleClose, item, onMoved }) => {
  const dispatch = useDispatch();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [search, setSearch] = useState("");
  // ids of expanded folders
  const [expanded, setExpanded] = useState(() => new Set());
  // undefined = nothing selected yet; null = root
  const [destination, setDestination] = useState(undefined);

  useEffect(() => {
    if (!open) return;
    setDestination(undefined);
    setSearch("");
    setExpanded(new Set());
    setLoading(true);
    GetAllFoldersService()
      .then((res) => {
        setFolders(res.data?.data || []);
      })
      .catch((e) => {
        dispatch(
          showSnackbar(
            e.response?.data?.message || "Failed to load folders",
            "error"
          )
        );
      })
      .finally(() => setLoading(false));
  }, [open]);

  // Folder rows, minus the folder being moved and its own subtree.
  const options = useMemo(() => buildFolderOptions(folders, item), [folders, item]);

  // findSearchRoots returns { matches, roots } - rename on the way in.
  const { matches, roots: searchRoots } = useMemo(
    () => findSearchRoots(options, search),
    [options, search]
  );

  // Open the matches themselves so their sub-folders are one click away, while
  // deeper levels stay collapsed and the result list stays short.
  useEffect(() => {
    if (!searchRoots?.length) return;
    setExpanded((prev) => new Set([...prev, ...searchRoots.map((r) => r.id)]));
  }, [searchRoots]);

  const visibleOptions = useMemo(
    () => buildVisibleRows({ options, search, expanded, roots: searchRoots }),
    [options, search, expanded, searchRoots]
  );

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMove = async () => {
    if (destination === undefined || !item?.id) return;
    setMoving(true);
    try {
      const res =
        item.type === "folder"
          ? await UpdateFolderService({ parent: destination }, item.id)
          : await UpdateFileService({ folder: destination }, item.id);
      if (res.status === 200) {
        dispatch(
          showSnackbar(res.data?.message || "Moved successfully", "success")
        );
        onMoved?.();
        handleClose();
      }
    } catch (e) {
      dispatch(
        showSnackbar(e.response?.data?.message || "Failed to move", "error")
      );
    } finally {
      setMoving(false);
    }
  };

  const isSearching = Boolean(search.trim());

  return (
    <Modal open={Boolean(open)} onClose={handleClose}>
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography
            fontFamily={FontFamily.Poppins}
            fontWeight={fontWeight.W600}
            fontSize={fontSize.h3.large}
            color={ThemeColor.BLACK}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Move "{item?.name}" to...
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ color: "#667085" }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search folders..."
          size="small"
          fullWidth
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontFamily: FontFamily.Poppins,
              fontSize: fontSize.h4.large,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#98a2b3" }} fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            border: "1px solid #e4e7ec",
            borderRadius: "10px",
            minHeight: 240,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 240,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense disablePadding>
              {!isSearching && (
                <ListItemButton
                  selected={destination === null}
                  onClick={() => setDestination(null)}
                >
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <HomeRounded sx={{ color: ThemeColor.PURPLE }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      fontFamily: FontFamily.Poppins,
                      fontWeight: fontWeight.W600,
                      fontSize: fontSize.h4.large,
                    }}
                    primary="My Folder (Root)"
                  />
                </ListItemButton>
              )}
              {visibleOptions.map((opt) => (
                <ListItemButton
                  key={opt.id}
                  selected={destination === opt.id}
                  onClick={() => setDestination(opt.id)}
                  sx={{ pl: 1 + opt.depth * 2.5, py: 0.75 }}
                >
                  <Box
                    sx={{
                      width: 28,
                      display: "flex",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {opt.hasChildren && (
                      <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(opt.id);
                        }}
                      >
                        {expanded.has(opt.id) ? (
                          <ExpandMore fontSize="small" />
                        ) : (
                          <ChevronRight fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Box>
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <FolderRounded sx={{ color: ThemeColor.YELLOW }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      fontFamily: FontFamily.Poppins,
                      fontSize: fontSize.h4.large,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    primary={opt.name}
                    secondary={
                      isSearching && matches.has(opt.id) ? opt.path : null
                    }
                    secondaryTypographyProps={{
                      fontFamily: FontFamily.Poppins,
                      fontSize: "0.75rem",
                      color: "#98a2b3",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </ListItemButton>
              ))}
              {!loading && isSearching && visibleOptions.length === 0 && (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography
                    fontFamily={FontFamily.Poppins}
                    fontSize={fontSize.h4.large}
                    color={ThemeColor.GREY}
                  >
                    No folders found for "{search.trim()}"
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              color: "#667085",
              borderColor: "#d0d5dd",
              "&:hover": {
                borderColor: "#98a2b3",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <CustomButton
            variant="contained"
            type="button"
            backgroundColor={ThemeColor.PURPLE}
            color={ThemeColor.WHITE}
            disabled={destination === undefined || moving}
            loading={moving}
            onClick={handleMove}
          >
            Move Here
          </CustomButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default MoveToModal;
